// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// Patent App No. ZA 2026/04523 · Priority Date: 08 October 2025
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { Plus, ShieldCheck, Trash2, RefreshCw, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  { value: "global", label: "Global default" },
  { value: "bank", label: "Per-bank override" },
  { value: "account_type", label: "Per-account-type" },
  { value: "customer_tier", label: "Per-customer-tier" },
];

const zar = (a) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", minimumFractionDigits: 2 }).format(a || 0);

const DepositLimits = () => {
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    scope: "bank",
    scope_key: "",
    single_transaction_limit: 24999,
    daily_cumulative_limit: 49999,
    requires_edd_above: 24999,
    is_active: true,
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("360wallet_token");
      const res = await axios.get(`${API_URL}/api/admin/deposit-limits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLimits(res.data.limits || []);
    } catch (e) {
      toast.error("Failed to load deposit limits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.scope_key) {
      toast.error("scope_key is required (e.g. 'default', bank code, tier name)");
      return;
    }
    try {
      const token = localStorage.getItem("360wallet_token");
      await axios.post(`${API_URL}/api/admin/deposit-limits`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Saved ${form.scope}/${form.scope_key}`);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const del = async (scope, scope_key) => {
    if (!window.confirm(`Deactivate limit rule ${scope}/${scope_key}?`)) return;
    try {
      const token = localStorage.getItem("360wallet_token");
      await axios.delete(`${API_URL}/api/admin/deposit-limits/${scope}/${scope_key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const openForNew = (scope) => {
    setForm({
      scope,
      scope_key: scope === "global" ? "default" : "",
      single_transaction_limit: 24999,
      daily_cumulative_limit: 49999,
      requires_edd_above: 24999,
      is_active: true,
      notes: "",
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="deposit-limits-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            ATM Deposit Limits
          </h1>
          <p className="text-muted-foreground">
            Configurable FICA/FIC compliant limits · Global default + per-bank/account-type/customer-tier overrides
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-limit-btn">
                <Plus className="h-4 w-4 mr-2" /> New Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Deposit Limit Rule</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Scope</Label>
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, scope_key: v === "global" ? "default" : "" })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SCOPES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Scope Key</Label>
                    <Input
                      value={form.scope_key}
                      onChange={(e) => setForm({ ...form, scope_key: e.target.value })}
                      placeholder={
                        form.scope === "global" ? "default" :
                        form.scope === "bank" ? "ABSA" :
                        form.scope === "account_type" ? "savings" :
                        "tier1"
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Single Transaction Limit (ZAR)</Label>
                  <Input
                    type="number"
                    value={form.single_transaction_limit}
                    onChange={(e) => setForm({ ...form, single_transaction_limit: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Daily Cumulative Limit (ZAR) — 0 = no limit</Label>
                  <Input
                    type="number"
                    value={form.daily_cumulative_limit}
                    onChange={(e) => setForm({ ...form, daily_cumulative_limit: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Requires EDD Above (ZAR) — 0 = no EDD</Label>
                  <Input
                    type="number"
                    value={form.requires_edd_above}
                    onChange={(e) => setForm({ ...form, requires_edd_above: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="e.g. Updated per FIC directive 2026-08"
                    rows={2}
                  />
                </div>
                <Button className="w-full" onClick={save} data-testid="save-limit-btn">
                  Save Rule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Fallback hierarchy explainer */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium mb-1">Fallback hierarchy (highest priority first):</div>
            <div className="text-muted-foreground">
              <span className="font-mono">customer_tier</span> → <span className="font-mono">account_type</span> →{" "}
              <span className="font-mono">bank</span> → <span className="font-mono">global/default</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Regulatory changes (SARB, FIC, participating banks) can be applied here without any code deploy.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rules table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Active Rules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3">Scope</th>
                  <th className="text-left py-2 px-3">Key</th>
                  <th className="text-right py-2 px-3">Single Tx</th>
                  <th className="text-right py-2 px-3">Daily</th>
                  <th className="text-right py-2 px-3">EDD Above</th>
                  <th className="text-left py-2 px-3">Notes</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-muted-foreground">
                      <RefreshCw className="h-4 w-4 animate-spin inline mr-2" />Loading...
                    </td>
                  </tr>
                )}
                {!loading && limits.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-muted-foreground">
                      No rules configured — global default falls back to R24,999 single-tx.
                    </td>
                  </tr>
                )}
                {limits.filter((l) => l.is_active).map((l) => (
                  <tr key={`${l.scope}-${l.scope_key}`} className="border-b border-border/50">
                    <td className="py-2 px-3">
                      <Badge variant="outline" className={
                        l.scope === "global" ? "text-purple-400" :
                        l.scope === "bank" ? "text-blue-400" :
                        l.scope === "account_type" ? "text-green-400" :
                        "text-yellow-400"
                      }>
                        {l.scope}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 font-mono">{l.scope_key}</td>
                    <td className="py-2 px-3 text-right font-mono">{zar(l.single_transaction_limit)}</td>
                    <td className="py-2 px-3 text-right font-mono">
                      {l.daily_cumulative_limit > 0 ? zar(l.daily_cumulative_limit) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">
                      {l.requires_edd_above > 0 ? zar(l.requires_edd_above) : <span className="text-muted-foreground">no EDD</span>}
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{l.notes || "—"}</td>
                    <td className="py-2 px-3 text-right">
                      {!(l.scope === "global" && l.scope_key === "default") && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => del(l.scope, l.scope_key)}
                          data-testid={`delete-${l.scope}-${l.scope_key}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DepositLimits;
